import { Checkbox } from "../../../../components/Checkbox";
import { Button } from "../../../../components/Button";
import { useEffect, useMemo, useState } from "react";
import type {
  CalendarEventResponse,
  OrganizationMember,
  TeamSearchResult,
} from "../../../../types/api";
import {
  utcDateInputValue,
  exclusiveEndToInclusiveDate,
  utcToZonedInput,
  zonedInputToUtcIso,
} from "../../../../utils/calendarTime";
import { searchTeams } from "../../../../services/teams";
import {
  searchLocations,
  getLocationDetails,
  type LocationSuggestion,
} from "../../../../services/calendar";
import { Avatar } from "./CoachAvatar";
import "./ScheduleFields.css";
import { useDismiss } from "./useDismiss";

export interface PickedLocation {
  name: string;
  address: string | null;
  lat: number | null;
  lng: number | null;
  placeId: string | null;
}

/**
 * Click-to-edit location with Google Places autocomplete. Shows the current name
 * as plain text until clicked; typing (3+ chars, debounced) lists suggestions;
 * picking one resolves its address/coords and reports the full location up.
 */
export function LocationField({
  name,
  placeholder = "Add a location",
  onPick,
}: {
  name: string;
  placeholder?: string;
  onPick: (loc: PickedLocation) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [query, setQuery] = useState(name);
  const [suggestions, setSuggestions] = useState<LocationSuggestion[]>([]);
  const ref = useDismiss<HTMLDivElement>(editing, () => setEditing(false));

  useEffect(() => {
    if (!editing) setQuery(name);
  }, [name, editing]);

  useEffect(() => {
    if (!editing || query.trim().length < 3) {
      setSuggestions([]);
      return;
    }
    let active = true;
    const t = setTimeout(() => {
      searchLocations(query.trim())
        .then((s) => {
          if (active) setSuggestions(s);
        })
        .catch(() => {
          /* non-fatal: no suggestions */
        });
    }, 250);
    return () => {
      active = false;
      clearTimeout(t);
    };
  }, [query, editing]);

  const pick = async (s: LocationSuggestion) => {
    setEditing(false);
    try {
      const d = await getLocationDetails(s.placeId);
      onPick({
        name: s.description,
        address: d.formattedAddress || null,
        lat: d.lat,
        lng: d.lng,
        placeId: d.placeId,
      });
    } catch {
      onPick({ name: s.description, address: null, lat: null, lng: null, placeId: null });
    }
  };

  if (!editing) {
    return (
      <button
        type="button"
        className="inline-edit"
        onClick={() => setEditing(true)}
        title="Click to edit location"
      >
        {name ? name : <span className="inline-edit__ph">{placeholder}</span>}
      </button>
    );
  }
  return (
    <div className="sf" ref={ref}>
      <input
        autoFocus
        className="inline-edit__input"
        value={query}
        placeholder="Search a venue or address"
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Escape") setEditing(false);
        }}
      />
      {suggestions.length > 0 && (
        <div className="sf-menu sf-menu--scroll">
          {suggestions.map((s) => (
            <div
              key={s.placeId}
              role="option"
              aria-selected={false}
              className="sf-coach-option"
              onMouseDown={(e) => {
                e.preventDefault();
                void pick(s);
              }}
            >
              {s.description}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * A team-name field with a type-ahead over the org's existing teams (mirrors the
 * event location picker). Free text is allowed — picking a suggestion just fills
 * the name. Used by the game editors so team names aren't blind free-text.
 * Reuses the shared `.location-typeahead` / `.location-results` styling, which is
 * present in both the schedule (event view) and the wizard stylesheets.
 */
export function TeamNameField({
  value,
  onChange,
  ariaLabel,
  placeholder,
}: {
  value: string;
  onChange: (name: string) => void;
  ariaLabel?: string;
  placeholder?: string;
}) {
  const [open, setOpen] = useState(false);
  const [suggestions, setSuggestions] = useState<TeamSearchResult[]>([]);
  const ref = useDismiss(open, () => setOpen(false));

  useEffect(() => {
    if (!open || value.trim().length < 2) {
      setSuggestions([]);
      return;
    }
    const handle = setTimeout(() => {
      searchTeams(value.trim())
        .then(setSuggestions)
        .catch(() => setSuggestions([]));
    }, 250);
    return () => clearTimeout(handle);
  }, [value, open]);

  return (
    <div className="location-typeahead" ref={ref}>
      <input
        type="text"
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        placeholder={placeholder ?? "Team name"}
        aria-label={ariaLabel}
        autoComplete="off"
      />
      {open && suggestions.length > 0 && (
        <div className="location-results">
          {suggestions.map((t) => (
            <button
              type="button"
              key={t.id}
              className="location-result"
              // mousedown so the pick registers before the input blurs.
              onMouseDown={(e) => {
                e.preventDefault();
                onChange(t.name);
                setOpen(false);
              }}
            >
              {t.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const Chevron = ({ open }: { open: boolean }) => (
  <svg className={`sf-chevron${open ? " sf-chevron--open" : ""}`} width="11" height="11" viewBox="0 0 11 11" fill="none" aria-hidden>
    <path d="M2 3.5l3.5 3.5 3.5-3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// ---------- Date picker (custom calendar popover) ----------

const WEEKDAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
const parseYmd = (v: string): Date | null => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v);
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
};
const toYmd = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

/** Mini calendar-tile icon that shows the selected day number (matches the picked date). */
const MiniCalIcon = ({ date }: { date: Date | null }) => (
  <span className="sf-calicon" aria-hidden>
    <span className="sf-calicon__top" />
    <span className="sf-calicon__day">{date ? date.getDate() : "–"}</span>
  </span>
);

interface DatePickerFieldProps {
  value: string; // YYYY-MM-DD
  onChange: (v: string) => void;
  id?: string;
  /** Which edge the popover anchors to — use "right" for right-column fields so it can't clip. */
  align?: "left" | "right";
}

export const DatePickerField = ({ value, onChange, id, align = "left" }: DatePickerFieldProps) => {
  const [open, setOpen] = useState(false);
  const ref = useDismiss(open, () => setOpen(false));
  const selected = parseYmd(value);
  const [view, setView] = useState(() => selected ?? new Date());

  useEffect(() => {
    if (open && selected) setView(selected);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const label = selected
    ? selected.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
    : "Pick a date";

  const grid = useMemo(() => {
    const first = new Date(view.getFullYear(), view.getMonth(), 1);
    const start = first.getDay();
    const days = new Date(view.getFullYear(), view.getMonth() + 1, 0).getDate();
    const cells: (Date | null)[] = [];
    for (let i = 0; i < start; i++) cells.push(null);
    for (let d = 1; d <= days; d++) cells.push(new Date(view.getFullYear(), view.getMonth(), d));
    return cells;
  }, [view]);

  const todayYmd = toYmd(new Date());

  return (
    <div className="sf" ref={ref}>
      <button
        type="button"
        id={id}
        className={`sf-trigger${open ? " sf-trigger--open" : ""}${!selected ? " sf-trigger--empty" : ""}`}
        onClick={() => setOpen((v) => !v)}
      >
        <MiniCalIcon date={selected} />
        <span className="sf-trigger__label">{label}</span>
        <Chevron open={open} />
      </button>
      {open && (
        <div className={`sf-menu sf-cal${align === "right" ? " sf-cal--right" : ""}`}>
          <div className="sf-cal__head">
            <button type="button" className="sf-cal__nav" onClick={() => setView(new Date(view.getFullYear(), view.getMonth() - 1, 1))} aria-label="Previous month">‹</button>
            <span className="sf-cal__title">{view.toLocaleDateString("en-US", { month: "long", year: "numeric" })}</span>
            <button type="button" className="sf-cal__nav" onClick={() => setView(new Date(view.getFullYear(), view.getMonth() + 1, 1))} aria-label="Next month">›</button>
          </div>
          <div className="sf-cal__grid sf-cal__grid--dow">
            {WEEKDAYS.map((d) => (
              <span key={d} className="sf-cal__dow">{d}</span>
            ))}
          </div>
          <div className="sf-cal__grid">
            {grid.map((d, i) =>
              d === null ? (
                <span key={`b${i}`} />
              ) : (
                <button
                  key={toYmd(d)}
                  type="button"
                  className={`sf-cal__day${toYmd(d) === value ? " sf-cal__day--sel" : ""}${toYmd(d) === todayYmd ? " sf-cal__day--today" : ""}`}
                  onClick={() => {
                    onChange(toYmd(d));
                    setOpen(false);
                  }}
                >
                  {d.getDate()}
                </button>
              ),
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ---------- Time picker (15-min increments) ----------

const TIMES: { value: string; label: string }[] = (() => {
  const out: { value: string; label: string }[] = [];
  for (let h = 0; h < 24; h++) {
    for (const m of [0, 15, 30, 45]) {
      const value = `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
      const period = h < 12 ? "AM" : "PM";
      const h12 = h % 12 === 0 ? 12 : h % 12;
      out.push({ value, label: `${h12}:${String(m).padStart(2, "0")} ${period}` });
    }
  }
  return out;
})();
const timeLabel = (v: string) => TIMES.find((t) => t.value === v)?.label ?? v;

interface TimeSelectFieldProps {
  value: string; // HH:mm
  onChange: (v: string) => void;
  id?: string;
}

export const TimeSelectField = ({ value, onChange, id }: TimeSelectFieldProps) => {
  const [open, setOpen] = useState(false);
  const ref = useDismiss(open, () => setOpen(false));
  return (
    <div className="sf" ref={ref}>
      <button type="button" id={id} className={`sf-trigger${open ? " sf-trigger--open" : ""}`} onClick={() => setOpen((v) => !v)}>
        <span className="sf-trigger__icon" aria-hidden>🕑</span>
        <span className="sf-trigger__label">{timeLabel(value)}</span>
        <Chevron open={open} />
      </button>
      {open && (
        <div className="sf-menu sf-menu--scroll" role="listbox">
          {TIMES.map((t) => (
            <div
              key={t.value}
              role="option"
              aria-selected={t.value === value}
              className={`sf-option${t.value === value ? " sf-option--sel" : ""}`}
              onMouseDown={() => {
                onChange(t.value);
                setOpen(false);
              }}
            >
              {t.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ---------- Timezone picker ----------

const TZ_OPTIONS: { id: string; label: string }[] = [
  { id: "America/New_York", label: "Eastern (New York)" },
  { id: "America/Chicago", label: "Central (Chicago)" },
  { id: "America/Denver", label: "Mountain (Denver)" },
  { id: "America/Phoenix", label: "Arizona (Phoenix)" },
  { id: "America/Los_Angeles", label: "Pacific (Los Angeles)" },
  { id: "America/Anchorage", label: "Alaska (Anchorage)" },
  { id: "Pacific/Honolulu", label: "Hawaii (Honolulu)" },
];
const tzLabel = (id: string) => TZ_OPTIONS.find((t) => t.id === id)?.label ?? id;

interface TimezoneSelectFieldProps {
  value: string;
  onChange: (v: string) => void;
  id?: string;
}

export const TimezoneSelectField = ({ value, onChange, id }: TimezoneSelectFieldProps) => {
  const [open, setOpen] = useState(false);
  const ref = useDismiss(open, () => setOpen(false));
  // Ensure a value coming from a resolved place (any IANA id) is still selectable/visible.
  const options = TZ_OPTIONS.some((t) => t.id === value) || !value
    ? TZ_OPTIONS
    : [{ id: value, label: value }, ...TZ_OPTIONS];
  return (
    <div className="sf" ref={ref}>
      <button type="button" id={id} className={`sf-trigger${open ? " sf-trigger--open" : ""}`} onClick={() => setOpen((v) => !v)}>
        <span className="sf-trigger__icon" aria-hidden>🌎</span>
        <span className="sf-trigger__label">{tzLabel(value)}</span>
        <Chevron open={open} />
      </button>
      {open && (
        <div className="sf-menu sf-menu--scroll" role="listbox">
          {options.map((t) => (
            <div
              key={t.id}
              role="option"
              aria-selected={t.id === value}
              className={`sf-option${t.id === value ? " sf-option--sel" : ""}`}
              onMouseDown={() => {
                onChange(t.id);
                setOpen(false);
              }}
            >
              {t.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ---------- Coach multi-select with avatar circles ----------

interface CoachSelectFieldProps {
  members: OrganizationMember[];
  value: string[]; // selected user ids
  onChange: (value: string[]) => void;
  currentUserId?: string;
  /** Open the coach-name menu immediately (used when revealed behind a "+" button). */
  autoOpen?: boolean;
  /** Render ONLY the coach-name list — no chips row and no "+ Add coach" trigger
   *  (the caller supplies its own "+"). The list is always shown. */
  menuOnly?: boolean;
}

export const CoachSelectField = ({
  members,
  value,
  onChange,
  currentUserId,
  autoOpen = false,
  menuOnly = false,
}: CoachSelectFieldProps) => {
  const [open, setOpen] = useState(autoOpen || menuOnly);
  const ref = useDismiss(open && !menuOnly, () => setOpen(false));
  const byId = useMemo(() => new Map(members.map((m) => [m.id, m])), [members]);
  const toggle = (id: string) =>
    onChange(value.includes(id) ? value.filter((v) => v !== id) : [...value, id]);

  return (
    <div className={`sf sf--coach${menuOnly ? " sf--menu-only" : ""}`} ref={ref}>
      {!menuOnly && (
        <div className="sf-chips">
          {value.map((id) => {
            const m = byId.get(id);
            return (
              <span key={id} className="sf-coach-chip">
                <Avatar id={id} initials={m?.initials ?? "?"} ring={id === currentUserId} />
                {m?.displayName ?? "Unknown"}
                <button type="button" className="sf-coach-chip__rm" onClick={() => toggle(id)} aria-label={`Remove ${m?.displayName ?? "coach"}`}>×</button>
              </span>
            );
          })}
          <button type="button" className="sf-coach-add" onClick={() => setOpen((v) => !v)}>+ Add coach</button>
        </div>
      )}
      {(menuOnly || open) && (
        <div className="sf-menu sf-menu--scroll">
          {members.length === 0 && <div className="sf-empty">No coaches found.</div>}
          {members.map((m) => {
            const on = value.includes(m.id);
            return (
              <div
                key={m.id}
                role="option"
                aria-selected={on}
                className={`sf-coach-option${on ? " sf-coach-option--sel" : ""}`}
                onMouseDown={(e) => {
                  e.preventDefault();
                  toggle(m.id);
                }}
              >
                <Avatar id={m.id} initials={m.initials} ring={m.id === currentUserId} />
                <span className="sf-coach-option__name">{m.displayName}</span>
                {on && (
                  <svg className="sf-check" width="13" height="13" viewBox="0 0 12 12" fill="none" aria-hidden>
                    <path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

/**
 * All-day / timed date editor. Emits a patch (startsAtUtc/endsAtUtc/isAllDay/timeZone)
 * for the caller to persist. Shared by the event view and the Build Itinerary wizard so
 * both edit dates the same way.
 */
export function DateTimeEditor({
  event,
  onSave,
  onCancel,
}: {
  event: CalendarEventResponse;
  onSave: (patch: Partial<CalendarEventResponse>) => void;
  onCancel: () => void;
}) {
  const tzInit = event.timeZone || "America/New_York";
  const [isAllDay, setIsAllDay] = useState(event.isAllDay);
  const [startDate, setStartDate] = useState(utcDateInputValue(event.startsAtUtc));
  const [endDate, setEndDate] = useState(
    event.isAllDay
      ? exclusiveEndToInclusiveDate(event.endsAtUtc)
      : utcDateInputValue(event.endsAtUtc),
  );
  const [startTime, setStartTime] = useState(
    event.isAllDay ? "09:00" : utcToZonedInput(event.startsAtUtc, tzInit).split("T")[1] ?? "09:00",
  );
  const [endTime, setEndTime] = useState(
    event.isAllDay ? "10:00" : utcToZonedInput(event.endsAtUtc, tzInit).split("T")[1] ?? "10:00",
  );
  const [timeZone, setTimeZone] = useState(tzInit);
  const [error, setError] = useState("");

  const commit = () => {
    if (!startDate || !endDate) {
      setError("Pick a start and end date.");
      return;
    }
    if (isAllDay) {
      if (endDate < startDate) {
        setError("End can't be before start.");
        return;
      }
      const endsAtUtc = new Date(
        new Date(`${endDate}T00:00:00Z`).getTime() + 86_400_000,
      ).toISOString();
      onSave({ startsAtUtc: `${startDate}T00:00:00.000Z`, endsAtUtc, isAllDay: true });
      return;
    }
    if (!startTime || !endTime) {
      setError("Pick a start and end time.");
      return;
    }
    const startsAtUtc = zonedInputToUtcIso(`${startDate}T${startTime}`, timeZone);
    const endsAtUtc = zonedInputToUtcIso(`${endDate}T${endTime}`, timeZone);
    if (new Date(endsAtUtc) < new Date(startsAtUtc)) {
      setError("End can't be before start.");
      return;
    }
    onSave({ startsAtUtc, endsAtUtc, isAllDay: false, timeZone });
  };

  return (
    <div className="event-view__datetime">
      <Checkbox
        label="All-day / multi-day event"
        checked={isAllDay}
        onChange={(e) => setIsAllDay(e.target.checked)}
      />
      {isAllDay ? (
        <div className="event-editor__row">
          <div className="event-editor__field">
            <label htmlFor="schedule-fields-starts-0">Starts</label>
            <DatePickerField id="schedule-fields-starts-0" value={startDate} onChange={setStartDate} />
          </div>
          <div className="event-editor__field">
            <label htmlFor="schedule-fields-ends-1">Ends</label>
            <DatePickerField id="schedule-fields-ends-1" value={endDate} onChange={setEndDate} align="right" />
          </div>
        </div>
      ) : (
        <>
          <div className="event-editor__row">
            <div className="event-editor__field">
              <label htmlFor="schedule-fields-starts-2">Starts</label>
              <DatePickerField id="schedule-fields-starts-2" value={startDate} onChange={setStartDate} />
            </div>
            <div className="event-editor__field event-editor__field--time">
              <label>&nbsp;</label>
              <TimeSelectField value={startTime} onChange={setStartTime} />
            </div>
          </div>
          <div className="event-editor__row">
            <div className="event-editor__field">
              <label htmlFor="schedule-fields-ends-3">Ends</label>
              <DatePickerField id="schedule-fields-ends-3" value={endDate} onChange={setEndDate} />
            </div>
            <div className="event-editor__field event-editor__field--time">
              <label>&nbsp;</label>
              <TimeSelectField value={endTime} onChange={setEndTime} />
            </div>
          </div>
          <div className="event-editor__field">
            <label>Timezone</label>
            <TimezoneSelectField value={timeZone} onChange={setTimeZone} />
          </div>
        </>
      )}
      {error && (
        <div className="event-editor__error" role="alert">
          {error}
        </div>
      )}
      <div className="inline-edit__actions">
        <Button variant="primary"
          type="button"
          onClick={commit}
        >
          Save
        </Button>
        <Button variant="secondary" type="button"  onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}
